import { useMutation } from '@apollo/client';
import React, { useMemo, useState, useEffect } from 'react';
import { usePaginatedQuery } from '@playbookux/playbook-web-core';
import useDebounce from '../../../core/hooks/use-debounce';
import withDashboardSidebar from '../../components/with-dashboard-sidebar';
// components
import CustomTable from './custom-table';
// graphql
import {
  COMPANIES_LIMIT, DEBOUNCE_TIME, GET_ALL_COMPANIES, GET_MEETINGS, GET_PROJECTS_BY_COMPANY, GET_TEST_SESSIONS_BY_PROJECT,
  ORDER_COMPANIES_BY, SELECT_AND_PROCESS_MUTATION, ORDER_TEST_SESSIONS_BY, PROJECTS_LIMIT, ORDER_PROJECTS_BY,
} from './graphql';
// styles
import { Dropdown, Header, Loader } from './styles';

function ModeratedRecordings() {
  // searches
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedCompanySearch] = useDebounce(() => companySearch, DEBOUNCE_TIME, [companySearch]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [debouncedProjectSearch] = useDebounce(() => projectSearch, DEBOUNCE_TIME, [projectSearch]);

  const [selectedTestSession, setSelectedTestSession] = useState(null);

  const [selectAndProcess] = useMutation(SELECT_AND_PROCESS_MUTATION, {
    refetchQueries: [
      { query: GET_MEETINGS, variables: { testSessionId: selectedTestSession } },
    ],
  });

  useEffect(() => {
    setSelectedProject(null);
    setProjectSearch('');
  }, [selectedCompany]);

  useEffect(() => {
    setSelectedTestSession(null);
  }, [selectedProject]);

  // companies
  const attributes = {
    variables: {
      limit: COMPANIES_LIMIT,
      offset: 0,
      order: ORDER_COMPANIES_BY,
      search: debouncedCompanySearch,
    },
  };

  const {
    data: companyData = [],
    loading: loadingCompanies,
  } = usePaginatedQuery(GET_ALL_COMPANIES, attributes, { dataField: 'companies' });

  const workspaces = useMemo(() => {
    let newOptions = [];
    if (companyData && companyData.companies) {
      newOptions = companyData.companies.map(({ id, name }) => ({
        key: id,
        text: name,
        value: id,
      }));
    }
    return newOptions;
  }, [companyData]);

  const handleSearchChange = (_, { searchQuery = '' }) => {
    setCompanySearch(searchQuery.trim());
  };

  const handleChangeWorkspace = (value) => {
    if (value === '') {
      setSelectedCompany(null);
      setCompanySearch('');
    } else {
      setSelectedCompany(value);
    }
  };

  // projects
  const projectAttributes = {
    skip: !selectedCompany,
    variables: {
      limit: PROJECTS_LIMIT,
      offset: 0,
      order: ORDER_PROJECTS_BY,
      companyId: selectedCompany,
      name: debouncedProjectSearch,
    },
  };

  const {
    data: projectData = [],
    loading: loadingProjects,
  } = usePaginatedQuery(GET_PROJECTS_BY_COMPANY, projectAttributes, { dataField: 'projects' });

  const projects = useMemo(() => {
    let newOptions = [];
    if (projectData && projectData.projects) {
      newOptions = projectData.projects.map(({ id, name }) => ({
        key: id,
        text: name,
        value: id,
      }));
    }
    return newOptions;
  }, [projectData]);

  const handleProjectSearchChange = (_, { searchQuery = '' }) => {
    setProjectSearch(searchQuery.trim());
  };

  const handleChangeProject = (value) => {
    if (value === '') {
      setSelectedProject(null);
      setProjectSearch('');
    } else {
      setSelectedProject(value);
    }
  };

  // testSession
  const testSessionAttributes = {
    skip: !selectedProject,
    variables: {
      order: ORDER_TEST_SESSIONS_BY,
      projectId: selectedProject,
    },
  };

  const {
    data: testSessionData = [],
    loading: loadingTestSessions,
  } = usePaginatedQuery(GET_TEST_SESSIONS_BY_PROJECT, testSessionAttributes, { dataField: 'testSessions' });

  const testSessions = useMemo(() => {
    let newOptions = [];
    if (testSessionData && testSessionData.testSessions) {
      newOptions = testSessionData.testSessions.map(({ id, tester: { user: { profile: { firstName, lastName } } } }) => ({
        key: id,
        text: `${firstName} ${lastName}`,
        value: id,
      }));
    }
    return newOptions;
  }, [testSessionData]);

  const handleChangeTestSession = (value) => {
    setSelectedTestSession(value);
  };

  if (loadingCompanies || loadingProjects || loadingTestSessions) {
    return <Loader size="small" data-loader="moderated-recordings-loader" />;
  }

  const handleProcess = (meetingId) => {
    selectAndProcess({
      variables: {
        meetingId,
        testSessionId: selectedTestSession,
      },
    });
  };

  const handleDownload = (url) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <Header>
        <Dropdown
          loading={loadingCompanies}
          onChange={(_, { value }) => handleChangeWorkspace(value)}
          onSearchChange={handleSearchChange}
          options={workspaces}
          clearable
          search
          selection
          value={selectedCompany}
        />

        {selectedCompany && (
          <Dropdown
            clearable
            loading={loadingProjects}
            onChange={(_, { value }) => handleChangeProject(value)}
            onSearchChange={handleProjectSearchChange}
            options={projects}
            selection
            search
            value={selectedProject}
          />
        )}

        {selectedProject && (
          <Dropdown
            loading={loadingTestSessions}
            onChange={(_, { value }) => handleChangeTestSession(value)}
            options={testSessions}
            selection
            value={selectedTestSession}
          />
        )}
      </Header>

      {selectedTestSession
      && <CustomTable testSessionId={selectedTestSession} handleProcess={handleProcess} handleDownload={handleDownload} />}
    </>

  );
}

export default withDashboardSidebar(ModeratedRecordings);
